import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AccountTypes } from '../model/account-types';
import { ApiResponse } from '../model/api-response';
import { ApiIndicator } from '../model/apiindicator';
import { Branch } from '../model/branch';
import { CancellationReason } from '../model/cancellation-reason';
import { Company } from '../model/company';
import { ConfirmationType } from '../model/confirmation-type';
import { Country } from '../model/country';
import { Department } from '../model/department';
import { PaymentModes } from '../model/payment-modes';
import { PetitionCategories } from '../model/petition-categories';
import { TransactionStatus } from '../model/transaction-status';
import { User } from '../model/user';
import { DataService } from './data.service';
import { RecipientTypeId } from '../model/recipient-type-id';
import { RemittancesReasons } from '../model/remittance-reasons';
import { Relationship } from '../model/relationships';
import { SourceFunding } from '../model/source-funding';
import { OccupationIndustry } from '../model/occupation-industry';
import { SenderType } from '../model/sender-type';
import { City } from '../model/city';
import { Town } from '../model/town';
import { State } from '../model/state';
import { ReceivingChannelFilters } from '../model/receiving-channel-filter';
import { RemittancesReasonsByCountry } from '../model/remittance-reason-country';
import { RequestStatus } from '../model/maker-checker/request-status';
import { SearchOiStatus } from '../model/search-oi-status';
import { Currency } from '../model/currency';
import { OiTypes } from '../model/oi-types';
import { OiSouceCurrencies } from '../model/oi-source-currencies';
import { ExchangeCurrency } from '../model/exchange-currency';
import { OiRanks } from '../model/oi-ranks';

@Injectable({
  providedIn: 'root'
})
export class CatalogService {

  constructor(private dataService: DataService) { }

  getCompanies(): Observable<ApiResponse<Company[]>> {
    return this.dataService.getCatalog<Company[]>('/catalog/company/getcompanies')
  }

  getCountries(): Observable<ApiResponse<Country[]>> {
    return this.dataService.getCatalog<Country[]>('/catalog/country/getcountries')
  }

  getTransactionStatus(): Observable<ApiResponse<TransactionStatus[]>> {
    return this.dataService.getCatalog<TransactionStatus[]>('/catalog/transactionstatus/gettransactionstatus')
  }

  getEmployee(): Observable<ApiResponse<User[]>> {
    return this.dataService.getCatalog<User[]>('/catalog/xbcuser/getxbcusers')
  }

  getRspCode(): Observable<ApiResponse<Branch[]>> {
    return this.dataService.getCatalog<Branch[]>('/catalog/rspgroupbranch/getrspgroupbranches')
  }

  getModeOfPayments(): Observable<ApiResponse<PaymentModes[]>> {
    return this.dataService.getCatalog<PaymentModes[]>('/catalog/modeofpayment/getmodeofpayments')
  }

  getActiveModeOfPaymentsWithoutCash(): Observable<ApiResponse<PaymentModes[]>> {
    return this.dataService.getCatalog<PaymentModes[]>('/catalog/modeofpayment/getactivemodeofpaymentswithoutcash')
  }

  getDepartments(): Observable<ApiResponse<Department[]>> {
    return this.dataService.getCatalog<Department[]>('/catalog/groupdepartment/getgroupdepartments')
  }

  getPetitionCategory(): Observable<ApiResponse<PetitionCategories[]>> {
    return this.dataService.getCatalog<PetitionCategories[]>('/catalog/petition/getpetitioncategories')
  }

  getPreDefinedResponses(): Observable<any> {
    return this.dataService.getCatalog<any[]>('/catalog/petition/getpredefinedresponses')
  }

  postPetitionsPredefinedResponse(body: any): Observable<any> {
    return this.dataService.postCatalog<any>('/catalog/petition/postpetitionpredefinedresponse', body)
  }

  getApiIndicator(): Observable<ApiResponse<ApiIndicator[]>> {
    return this.dataService.getCatalog<ApiIndicator[]>('/catalog/processunpaidtransactions/getapiindicators')
  }

  getConfirmationType(): Observable<ApiResponse<ConfirmationType[]>> {
    return this.dataService.getCatalog<ConfirmationType[]>('/catalog/processunpaidtransactions/getconfirmationtypes')
  }

  getCancellationReason(statusId: number): Observable<ApiResponse<CancellationReason[]>> {
    return this.dataService.getCatalog<CancellationReason[]>('/catalog/cancellations/getcancellationreasons?statusId=' + statusId)
  }

  getTransactionAccountTypes(): Observable<ApiResponse<AccountTypes[]>> {
    return this.dataService.getCatalog<AccountTypes[]>('/catalog/transactiondetails/gettransactionaccounttypes')
  }

  getRecipientType(CountryId: string): Observable<ApiResponse<RecipientTypeId[]>> {
    return this.dataService.getCatalog<RecipientTypeId[]>('/catalog/transactiondetails/getrecipienttype?CountryId=' + CountryId);
  }

  getRelationshipWithBeneficiary(): Observable<ApiResponse<Relationship[]>> {
    return this.dataService.getCatalog<Relationship[]>('/catalog/common/getallrelationshipwithbeneficiaries');
  }
  
  getAllReasonRemittancesByCountry(payload: RemittancesReasonsByCountry): Observable<ApiResponse<RemittancesReasons[]>> {
    return this.dataService.postCatalog<RemittancesReasonsByCountry>('/catalog/common/postcountryreasonremittances', payload);
  }

  getSourceOfFunding(): Observable<ApiResponse<SourceFunding[]>> {
    return this.dataService.getCatalog<SourceFunding[]>('/catalog/transactiondetails/getsourceoffunding');
  }

  getOccupationIndustry(): Observable<ApiResponse<OccupationIndustry[]>> {
    return this.dataService.getCatalog<OccupationIndustry[]>('/catalog/transactiondetails/getoccupationindustry');
  }

  getSenderType(): Observable<ApiResponse<SenderType[]>> {
    return this.dataService.getCatalog<SenderType[]>('/catalog/common/getsendertypeid');
  }

  getStates(CountryId: string): Observable<ApiResponse<State[]>> {
    return this.dataService.getCatalog<State[]>('/catalog/state/getstatebycountryid?CountryId=' + CountryId);
  }

  getCities(payload: any): Observable<ApiResponse<City[]>> {
    return this.dataService.postCatalog<ApiResponse<City[]>>('/catalog/city/getcities', payload);
  }

  getTowns(payload: any): Observable<ApiResponse<Town[]>> {
    return this.dataService.postCatalog<ApiResponse<Town[]>>('/catalog/town/gettown', payload);
  }

  getModeOfPaymentsByFilter(filter: ReceivingChannelFilters): Observable<ApiResponse<PaymentModes[]>> {
    return this.dataService.postCatalog<PaymentModes[]>('/catalog/modeofpayment/getmodeofpaymentsbycitycountries', filter)
  }
  
  getMakerCheckerRequestStatus(): Observable<ApiResponse<RequestStatus[]>> {
    return this.dataService.getCatalog<RequestStatus[]>('/catalog/common/getmakerrequeststatus');
  }

  getOiStatuses(): Observable<ApiResponse<SearchOiStatus[]>> {
    return this.dataService.getCatalog<SearchOiStatus[]>('/catalog/searchoirsp/getoistatuses');
  }

  getOiCurrencies(): Observable<ApiResponse<Currency[]>> {
    return this.dataService.getCatalog<Currency[]>('/catalog/searchoirsp/getoicurrencies');
  }

  getOiTypes(): Observable<ApiResponse<OiTypes[]>> {
    return this.dataService.getCatalog<OiTypes[]>('/catalog/searchoirsp/getoitypes');
  }

  getOiSourceCurrencies(oiId: string): Observable<ApiResponse<OiSouceCurrencies[]>> {
    return this.dataService.getCatalog<OiSouceCurrencies[]>('/catalog/searchoirsp/getoisourcecurrencies?OiId=' + oiId);
  }

  getExchangeCurrencies(): Observable<ApiResponse<ExchangeCurrency[]>> {
    return this.dataService.getCatalog<ExchangeCurrency[]>('/catalog/exchangecurrencies/getexchangecurrencies');
  }

  getOiRanks(): Observable<ApiResponse<OiRanks[]>> {
    return this.dataService.getCatalog<OiRanks[]>('/catalog/oiranks/get');
  }

  createOiRank(rank : OiRanks) : Observable <ApiResponse<OiRanks>> {	
    return this.dataService.postCatalog<OiRanks>('/catalog/oiranks/create', rank)
  }

  deleteOiRank(rank : OiRanks) : Observable <ApiResponse<any>> {	
    return this.dataService.postCatalog<any>('/catalog/oiranks/delete?RankId='+rank.rankId, null)
  } 

  updateOiRank(rank : OiRanks): Observable<ApiResponse<any>> {
    return this.dataService.postCatalog('/catalog/oiranks/update', rank);
  }
  
}
